/**
 * PIQ 8.3.2: Source Type Normalizer
 * ==================================
 * Normalizes sourceType for items to ensure Google/Meta items
 * are always correctly identified regardless of how they arrive.
 */

import type { SourceType } from './operatorViewAdapter';

/**
 * PIQ 8.3.3: Normalize sourceType based on various indicators in the item.
 * Google/Meta items must ALWAYS be identified correctly for proper grouping.
 * 
 * PRIORITY ORDER (first match wins):
 * 1. Existing sourceType if already 'google' or 'meta'
 * 2. type/findingType field (contains 'google_ads_recommendation')
 * 3. metadata.origin
 * 4. metadata.google_customer_id
 * 5. sourceSystem / source / platform
 * 6. Title patterns
 */
export function normalizeSourceType(item: {
  sourceType?: SourceType | string;
  findingType?: string;
  type?: string;  // PIQ 8.3.3: Add type field (finding_type from backend)
  metadata?: Record<string, unknown>;
  title?: string;
  summary?: string;
  sourceSystem?: string;
  source?: string;
  platform?: string;
  origin?: string;
  aiEvidence?: { dataSource?: string };
}): SourceType {
  // Already correctly typed
  if (item.sourceType === 'google') return 'google';
  if (item.sourceType === 'meta') return 'meta';
  
  // PIQ 8.3.3: Check type field (this is finding_type from backend)
  // This is the MOST RELIABLE indicator
  const findingType = item.type || item.findingType || '';
  if (findingType === 'google_ads_recommendation' || findingType.startsWith('google_ads')) return 'google';
  if (findingType === 'meta_ads_recommendation' || findingType.startsWith('meta_ads')) return 'meta';
  
  // Check metadata.origin
  if (item.metadata?.origin === 'google_ads_recommendation') return 'google';
  if (item.metadata?.origin === 'google_ads') return 'google';
  if (item.metadata?.origin === 'meta_ads_recommendation') return 'meta';
  if (item.metadata?.origin === 'meta_ads') return 'meta';
  
  // Check for Google customer ID in metadata (strong indicator)
  if (item.metadata?.google_customer_id || item.metadata?.googleCustomerId) return 'google';
  if (item.metadata?.meta_account_id || item.metadata?.metaAccountId) return 'meta';
  
  // Check sourceSystem
  if (item.sourceSystem === 'google_ads' || item.sourceSystem === 'google') return 'google';
  if (item.sourceSystem === 'meta_ads' || item.sourceSystem === 'meta' || item.sourceSystem === 'facebook') return 'meta';
  
  // Check source
  if (item.source === 'google' || item.source === 'google_ads') return 'google';
  if (item.source === 'meta' || item.source === 'meta_ads' || item.source === 'facebook') return 'meta';
  
  // Check platform
  if (item.platform === 'google' || item.platform === 'google_ads') return 'google';
  if (item.platform === 'meta' || item.platform === 'facebook' || item.platform === 'instagram') return 'meta';
  
  // Check origin
  if (item.origin === 'google' || item.origin === 'google_ads') return 'google';
  if (item.origin === 'meta' || item.origin === 'facebook') return 'meta';
  
  // Check aiEvidence.dataSource
  if (item.aiEvidence?.dataSource?.includes('google_ads')) return 'google';
  if (item.aiEvidence?.dataSource?.includes('meta_ads')) return 'meta';
  
  // Check title patterns
  const title = item.title || item.summary || '';
  if (title.startsWith('Google Ads:') || title.startsWith('Google:')) return 'google';
  if (title.startsWith('Meta Ads:') || title.startsWith('Meta:') || title.startsWith('Facebook:')) return 'meta';
  
  // PIQ 8.3.3: Check for "Increase campaign budget" with any Google indicator
  // This is the specific case from the bug report
  if (title.toLowerCase().includes('increase campaign budget')) {
    // If ANY Google indicator exists, this is a Google rec
    if (item.metadata?.google_customer_id || 
        item.metadata?.recommendationType || 
        item.metadata?.origin?.toString().includes('google') ||
        item.aiEvidence?.dataSource?.includes('google')) {
      return 'google';
    }
  }
  
  // PIQ 8.3.3: Data quality items -> system
  if (title.toLowerCase().includes('data discrepanc') || 
      title.toLowerCase().includes('resolve data') ||
      findingType.includes('data_quality')) {
    return 'system';
  }
  
  // Default: return existing sourceType or fallback to 'piq'
  const existing = item.sourceType as SourceType;
  if (existing && ['piq', 'forecast', 'pattern', 'manual', 'system'].includes(existing)) {
    return existing;
  }
  
  return 'piq';
}

/**
 * Check if an item is a Google/Meta marketing item
 */
export function isMarketingSource(sourceType: SourceType): boolean {
  return sourceType === 'google' || sourceType === 'meta';
}

/**
 * Normalize an entire array of items, ensuring Google/Meta are correctly typed
 */
export function normalizeItemsSources<T extends { sourceType?: SourceType | string }>(
  items: T[]
): (T & { sourceType: SourceType })[] {
  return items.map(item => ({
    ...item,
    sourceType: normalizeSourceType(item as any),
  }));
}
